import * as React from "react"

import { useDebounce } from "@smartdevis/ui/src/hooks/useDebounce"
import { useNullableState } from "../../../hooks/utilityHooks"
import { asyncConnect } from "../../../resolvers"
import { i18n } from "../../../services/translations"
import { IconButton, Button } from "@smartdevis/ui/src/Button"
import { H4, P, H5 } from "@smartdevis/ui/src/Typography"
import { FlexItem, VerticalSpace, FlexColumn, FlexRow, HorizontalSpace } from "@smartdevis/ui/src/utils/common"
import { Join } from "@smartdevis/ui/src/utils/Join"
import { Input } from "@smartdevis/ui/src/Inputs"
import { Collapsable } from "@smartdevis/ui/src/Collapsable"
import { Empty } from "@smartdevis/ui/src/Empty"
import { CatalogRef, NPKCatalogs } from "@smartdevis/server/src/domain"
import { values } from "@smartdevis/utils/src/map"
import { isDefined } from "@smartdevis/utils/src/misc"
import { F1, F0, F2 } from "@smartdevis/utils/src/types"
import { IdLite } from "@smartdevis/utils/src/id"
import { ShelfType, Version } from "@smartdevis/server/src/domainCatalog"
import { Spinner } from "@smartdevis/ui/src/Spinner"
import { CatalogCollapsableItem } from "../devis-catalogs/devis-catalogs-styles"
import { DropDownHandler } from "../npk-catalogs/npk-catalogs-language"
import { CrbCatalogPickerMode, Ref } from "./helper/crb-catalog-picker"
import { getLicenceIDs, getNPKLanguages, getNPKVersion, getSectionsList } from "./helper"
import { formatNPKNumber, setABBNodes } from "./helper/utils"


type CatalogSearchProps = {
    shelfType: ShelfType
    onBrowseCatalog: F1<IdLite>
    onBrowseShelf: F0
    pickerMode: CrbCatalogPickerMode
    setPickerMode: F1<CrbCatalogPickerMode>
    // onSelect: F2<string, NPKCatalogs.NPKNode>
    shelfVersion: Version
    credentials: NPKCatalogs.Credentails
    selectedCatalogRef?: Ref | null
    setCredentials: F1<NPKCatalogs.Credentails>
    navigation: NPKCatalogs.ChapterRef
    setNavigation: F1<NPKCatalogs.ChapterRef>
}

export const NPKCatalogSearch = asyncConnect({
    stateResolvers: ["NPKLanguages", "user", "auth", "NPKChapters", "NPKVersionYears"],
    actions: ["fetchNPKLanguages", "fetchNPKVersionYears"]
})<CatalogSearchProps>(p => {
    const [licenseIds, setLicenseIds] = React.useState<Array<string>>();
    const [languages, setLanguages] = React.useState<NPKCatalogs.Languages>();
    const [versions, setVersions] = React.useState<NPKCatalogs.VersionYears>();

    React.useEffect(() => {
        if (!licenseIds) {
            getLicenceIDs(p.auth, { customerID: p.credentials.customerID, licenseID: p.credentials.licenseID }).then((res) => {
                const licenseIds = (res.value as NPKCatalogs.LicenseIds).map(i => i.licenceID);
                setLicenseIds(licenseIds);
            });
        }

        if (!languages) {
            getNPKLanguages(p.auth).then((res) => {
                const languages = res.value as NPKCatalogs.Languages;
                setLanguages(languages);
            });
        }

        if (!versions) {
            getNPKVersion(p.auth).then((res) => {
                const versions = res.value as Array<number>;
                setVersions(versions);
            });
        }
    }, [languages, versions, licenseIds]);

    // const [query, setQuery] = useNullableState<string>()
    // const search = useDebounce(
    //     (text: string) => isDefined(text) ? chapters.filter(e => e.display.includes(text)) : [],
    //     500
    // )

    // const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const q = e.target.value
    //     setQuery(q)
    //     search.run(q)
    // }

    const renderBrowser = () => {
        // const showChildren = !query || p.pickerMode.force
        // if (!showChildren) return <CatalogSearchResults chapters={chapters} startedSearch={query !== null} {...p} />

        switch (p.pickerMode.type) {
            case "browseShelf":
                return (
                    <ShelfBrowser
                        shelfType={p.shelfType}
                        shelfVersion={p.shelfVersion}
                        credentials={p.credentials}
                        onCatalogSelect={(catalog: NPKCatalogs.Chapter) => {
                            p.setPickerMode({ type: "browseCatalog", catalogId: catalog.number }), p.setNavigation({ ...p.navigation, number: catalog.number, chapterTitle: catalog.display })
                        }}
                        navigation={p.navigation}
                        setNavigation={p.setNavigation}
                    />
                )
            case "browseCatalog":
                return (
                    <CatalogBrowser
                        shelfType={p.shelfType}
                        shelfVersion={p.shelfVersion}
                        selectedCatalogRef={p.selectedCatalogRef}
                        onBack={() => p.setPickerMode({ type: "browseShelf" })}
                        navigation={p.navigation}
                        setNavigation={p.setNavigation}
                    />
                )
        }
    }
    return (
        <FlexItem overflow="hidden" direction="column">
            <FlexRow>
                <DropDownHandler currentValue={p.credentials.licenseID} results={licenseIds ?? []} setValue={(licenseID) => p.setCredentials({ ...p.credentials, licenseID })} />
                <DropDownHandler currentValue={p.navigation.language} results={languages ?? []} setValue={(language) => p.setNavigation({ ...p.navigation, language })} />
                <DropDownHandler currentValue={p.navigation.version} results={versions ?? []} setValue={(version) => p.setNavigation({ ...p.navigation, version })} />
                {/* <Input
                    placeholder={i18n("Search in catalogs, positions and attributes")}
                    data-cy="positions-search-catalog-input"
                    value={query || ""}
                    onChange={handleChange}
                /> */}
            </FlexRow>
            <VerticalSpace base="16px" />
            <FlexItem overflow="auto" direction="column" yAlign="flex-start">
                {renderBrowser()}
            </FlexItem>
        </FlexItem>
    )
})

type CatalogSearchResultsProps = CatalogSearchProps & { startedSearch: boolean, chapters: NPKCatalogs.Chapters }
const CatalogSearchResults = asyncConnect({ stateResolvers: [], actions: [] })<CatalogSearchResultsProps>(p => {
    const matches = (p.chapters)
    if (!matches.length)
        return (
            <Empty
                type="node"
                value={
                    <FlexColumn alignCenter>
                        {p.startedSearch ? <H4>{i18n("No results")}</H4> : <H4>{i18n("Catalogs")}</H4>}
                        <P>
                            {i18n("Search for construction work descriptions and add them easily to the devi's.")}
                        </P>
                        <VerticalSpace base="16px" />
                        <IconButton
                            icon="Search"
                            onClick={p.onBrowseShelf}
                            data-cy="positions-search-catalog-browse-all">
                            {i18n("Browse all catalogs")}
                        </IconButton>
                    </FlexColumn>
                }
            />
        )
    return (
        <>
            {matches.map(v => (
                <Collapsable title={v.display} sideText={v.display} key={v.number}>
                    <Join items={values(v.positions)} renderJoining={() => <VerticalSpace base="8px" />}>
                        {vp => (
                            <CatalogCollapsableItem
                                isActive={vp.key === p.selectedCatalogRef?.workTitleKey}
                                onClick={() => p.onSelect(v.number as IdLite, vp)}
                                key={vp.key}>
                                {vp.title}
                            </CatalogCollapsableItem>
                        )}
                    </Join>
                    <VerticalSpace base="8px" />
                    <Button
                        btnType="link"
                        onClick={() => p.onBrowseCatalog(v.number as IdLite)}
                        data-cy="positions-search-catalog-browse-all">
                        {i18n("Browse catalog")}
                    </Button>
                </Collapsable>
            ))}
        </>
    )
})

type CatalogBrowserProps = Pick<CatalogSearchProps, "selectedCatalogRef" | "shelfType"> & {
    shelfVersion: Version,
    onBack: F0
    navigation: NPKCatalogs.ChapterRef
    setNavigation: F1<NPKCatalogs.ChapterRef>
}
const CatalogBrowser = asyncConnect({ stateResolvers: ["auth"], actions: [] })<CatalogBrowserProps>(p => {

    const [sections, setSections] = React.useState<Array<Pick<NPKCatalogs.Chapter, "number" | "display">>>();

    const fetchSectionsList = () => getSectionsList(p.auth, {
        chapter: p.navigation.number,
        language: p.navigation.language,
        year: p.navigation.version.toString()
    }).then((res) => {
        const sectionsList = res.value as Array<Pick<NPKCatalogs.Chapter, "number" | "display">>;
        setSections(sectionsList);
    }).catch(err => err);

    React.useEffect(() => {
        if (!sections) {
            fetchSectionsList();
        }
    }, [sections])

    React.useEffect(() => {
        fetchSectionsList();
    }, [p.navigation.language])


    // switch (p.NPKChapterDetails.type) {
    //     case "NotFetched": return <></>
    //     case "Fetching": return <Spinner />
    //     case "Fetched":
    // const sectionsLists: Array<NPKCatalogs.Chapter> = p.NPKChapterDetails.value

    if (!sections) return <Spinner />
    return (
        <FlexItem direction="column" overflow="hidden">
            <FlexRow alignCenter>
                <IconButton icon="ArrowLeft" onClick={p.onBack} />
                <HorizontalSpace base="16px" />
                <H5>{p.navigation.chapterTitle}</H5>
            </FlexRow>
            <VerticalSpace base="16px" />
            <FlexItem overflow="scroll" direction="column" yAlign="flex-start">
                {/* {(chapterDetails.subNodes?.npknode || []).map(node => renderCatalogItem(node))} */}
                <Join items={sections ?? []} renderJoining={() => <VerticalSpace base="16px" />}>
                    {s => (
                        <CatalogCollapsableItem
                            isActive={s.number === p.navigation.snumber}
                            onClick={() => p.setNavigation({ ...p.navigation, snumber: s.number })} key={s.number}>
                            {formatNPKNumber(s.number)} {s.display}
                        </CatalogCollapsableItem>
                    )}
                </Join>
            </FlexItem>
        </FlexItem>
    )
    // }

})

type ShelfBrowserProps = Pick<CatalogSearchProps, "shelfType"> & {
    onCatalogSelect: F1<NPKCatalogs.Chapter>
    shelfVersion: Version
    credentials: NPKCatalogs.Credentails
    navigation: NPKCatalogs.ChapterRef
    setNavigation: F1<NPKCatalogs.ChapterRef>
}
const ShelfBrowser = asyncConnect({ stateResolvers: ["NPKChapters"], actions: ["fetchNPKChapters"] })<ShelfBrowserProps>(p => {

    React.useEffect(() => {
        if (p.NPKChapters.type === "NotFetched") {
            p.fetchNPKChapters({ ...p.credentials, displayLanguage: p.navigation.language })

        }
    }, [p.NPKChapters])

    React.useEffect(() => {
        p.fetchNPKChapters({ ...p.credentials, displayLanguage: p.navigation.language, licenseID: p.credentials.licenseID })
    }, [p.navigation.language, p.navigation.version, p.credentials.licenseID])

    switch (p.NPKChapters.type) {
        case "NotFetched": return <></>
        case "Fetching": return <Spinner />
        case "Fetched":
            const chapters = p.NPKChapters.value;
            return (<Join items={chapters} renderJoining={() => <VerticalSpace base="16px" />}>
                {c => (
                    <CatalogCollapsableItem onClick={() => p.onCatalogSelect(c)} key={c.number}>
                        {c.display}
                    </CatalogCollapsableItem>
                )}
            </Join>)
    }


})
